/**
 * PreviewOriginGuard — keeps users away from installing the PWA from the
 * preview (Emergent dev) URL. Detection is hostname-based:
 *
 *   - Production:  neuracom.ai (or www.neuracom.ai)
 *   - Preview:     *.preview.emergentagent.com  ← shows the banner
 *
 * The banner stays sticky at the top, full width, on EVERY page until the
 * user clicks the CTA (which navigates to production preserving the path
 * & query) OR dismisses it via the small close button.
 *
 * Why this exists: a user installed the PWA from the preview URL, the
 * Android WebAPK ended up signed against the *.emergentagent.com host, and
 * Google Play Protect / the preview-injected Emergent watermark caused
 * confusion. This guard prevents the same mistake for future users.
 */
import React, { useState } from "react";
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";

const PRODUCTION_HOST = "neuracom.ai";
const DISMISS_KEY = "neuracom_preview_banner_dismissed";

function isPreviewHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  // production hosts
  if (h === PRODUCTION_HOST || h === "www." + PRODUCTION_HOST) return false;
  // localhost is fine for dev
  if (h === "localhost" || h === "127.0.0.1") return false;
  // anything *.preview.emergentagent.com or *.emergentagent.com is preview
  return h.includes("emergentagent.com") || h.includes(".preview.");
}

function buildProductionUrl() {
  const path = window.location.pathname + window.location.search + window.location.hash;
  return `https://${PRODUCTION_HOST}${path}`;
}

export function PreviewOriginGuard() {
  const [visible, setVisible] = useState(false);

  useImperativeLoad(() => {
    if (!isPreviewHost()) return;
    try {
      const dismissed = sessionStorage.getItem(DISMISS_KEY);
      if (!dismissed) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-x-0 top-0 z-[10000] border-b border-amber-400/40 bg-amber-500/95 text-[#05073B] shadow-lg backdrop-blur"
      role="alert"
      data-testid="preview-origin-guard"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 sm:px-6">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1 text-xs leading-snug sm:text-sm">
          <span className="font-bold">Development environment.</span>{" "}
          <span className="hidden sm:inline">
            You&apos;re on the preview. For the stable version and to install the PWA correctly, go to{" "}
          </span>
          <span className="font-mono font-bold">neuracom.ai</span>
        </div>
        <a
          href={buildProductionUrl()}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#05073B] px-3 py-1 text-[11px] font-bold text-white hover:bg-[#1A1D7A] sm:text-xs"
          data-testid="preview-guard-cta"
        >
          Open production
          <ArrowRight className="h-3 w-3" />
        </a>
        <button
          onClick={dismiss}
          className="shrink-0 rounded-full p-1 text-[#05073B] hover:bg-amber-600/30"
          aria-label="Dismiss"
          data-testid="preview-guard-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
